<template>
  <div class="editor-global-filter" @keydown.stop="handleKey" @focusout="handleFocusOut">
    <button ref="trigger" type="button" class="editor-icon-button" :class="{ 'is-active': open }" :disabled="!ready"
            aria-label="全局滤镜" title="全局滤镜" aria-haspopup="dialog" :aria-expanded="String(open)" aria-controls="editor-global-filter-panel" @click="toggle">
      <i class="el-icon-magic-stick" aria-hidden="true"></i>
    </button>
    <section v-if="open" id="editor-global-filter-panel" ref="panel" class="editor-global-filter__panel" role="dialog" aria-modal="false" aria-labelledby="editor-global-filter-title">
      <header class="editor-global-filter__head">
        <div><strong id="editor-global-filter-title">全局滤镜</strong><small>仅作用于当前页面画布</small></div>
        <button type="button" class="editor-global-filter__reset" @click="reset">重置</button>
        <button type="button" class="editor-icon-button" aria-label="关闭全局滤镜" @click="close()"><i class="el-icon-close" aria-hidden="true"></i></button>
      </header>
      <div class="editor-global-filter__controls">
        <label v-for="control in controls" :key="control.key" class="editor-global-filter__row" :for="'editor-filter-' + control.key">
          <span>{{ control.label }}</span>
          <input :id="'editor-filter-' + control.key" type="range" :data-filter="control.key" :min="control.min" :max="control.max" step="1" :value="values[control.key]"
                 :style="{ '--range-progress': ((values[control.key] - control.min) / (control.max - control.min) * 100) + '%' }"
                 @pointerdown="beginPointer" @keydown="beginKeyboard" @keyup="endKeyboard" @input="update(control.key, $event)" @change="change" @blur="finish">
          <input type="number" :min="control.min" :max="control.max" step="1" :value="values[control.key]" :aria-label="control.label + '数值'"
                 @input="update(control.key, $event)" @change="numberChange(control.key, $event)" @blur="numberBlur(control.key, $event)">
        </label>
      </div>
    </section>
  </div>
</template>

<script>
import { canvasFilterControls, normalizedCanvasFilter, canvasFilterContext, currentCanvasFilterContext, createCanvasFilterGesture } from '@/editor/core/global-filter'

export default {
  inject: ['contain'],
  data: () => ({ open: false, controls: canvasFilterControls }),
  computed: {
    ready () { return Boolean(this.contain.projectRecord && this.contain.editorSaveReady && !this.contain.editorVersionSwitching) },
    values () { return normalizedCanvasFilter(this.contain.config && this.contain.config.filter) }
  },
  watch: {
    open (value) { this.$emit('open-change', value) },
    'contain.activePageId' () { this.close(false) },
    'contain.config' (value, previous) { if (value !== previous) this.close(false) },
    'contain.projectRecord' (value, previous) { if (!value || !previous || value.id !== previous.id) this.close(false) },
    'contain.pageProject' (value, previous) { if (value !== previous) this.close(false) },
    'contain.id' () { this.close(false) },
    'contain.editorSaveGeneration' () { this.close(false) },
    ready (value) { if (!value) this.close(false) }
  },
  created () { this._gesture = createCanvasFilterGesture(this.contain) },
  mounted () {
    document.addEventListener('pointerdown', this.outside)
    document.addEventListener('pointerup', this.endPointer)
    document.addEventListener('pointercancel', this.endPointer)
    window.addEventListener('blur', this.finish)
  },
  beforeDestroy () {
    this.finish()
    document.removeEventListener('pointerdown', this.outside)
    document.removeEventListener('pointerup', this.endPointer)
    document.removeEventListener('pointercancel', this.endPointer)
    window.removeEventListener('blur', this.finish)
  },
  methods: {
    toggle () { this.open ? this.close() : this.show() },
    show () {
      if (!this.ready) return
      this._panelContext = canvasFilterContext(this.contain)
      this.open = true
      this.$nextTick(() => {
        if (!this.open || !this.$refs.panel) return
        const first = this.$refs.panel.querySelector('input[type="range"]')
        if (first) first.focus()
      })
    },
    close (restoreFocus = true) {
      this.finish()
      this._panelContext = null
      if (!this.open) return
      this.open = false
      if (restoreFocus) this.$nextTick(() => { if (!this._isDestroyed && this.$refs.trigger) this.$refs.trigger.focus() })
    },
    current () {
      if (this.open && currentCanvasFilterContext(this.contain, this._panelContext)) return true
      this.close(false)
      return false
    },
    update (key, event) { if (this.current()) this._gesture.update(key, event.target.value) },
    beginPointer (event) { if (this.current()) { this._pointerId = event.pointerId; this._gesture.begin() } },
    endPointer (event) { if (this._pointerId !== undefined && event.pointerId === this._pointerId) this.finish() },
    beginKeyboard (event) {
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown'].includes(event.key) && this.current()) {
        this._keyboardGesture = true
        this._gesture.begin()
      }
    },
    endKeyboard () { if (this._keyboardGesture) this.finish() },
    change () { if (this._pointerId === undefined && !this._keyboardGesture) this.finish() },
    numberChange (key, event) { this.update(key, event); event.target.value = this.values[key]; this.finish() },
    numberBlur (key, event) { event.target.value = this.values[key]; this.finish() },
    finish () { this._pointerId = undefined; this._keyboardGesture = false; if (this._gesture) this._gesture.end() },
    reset () { if (this.current()) this._gesture.reset() },
    outside (event) { if (!this.$el.contains(event.target)) this.close(false) },
    handleFocusOut (event) { if (event.relatedTarget && !this.$el.contains(event.relatedTarget)) this.close(false) },
    handleKey (event) {
      if (event.key === 'Escape') { event.preventDefault(); this.close() }
      else if (!this.open && ['ArrowDown', 'ArrowUp'].includes(event.key)) { event.preventDefault(); this.show() }
    }
  }
}
</script>

<style scoped>
.editor-global-filter { position: relative; flex-shrink: 0; }
.editor-global-filter > .is-active { color: var(--editor-accent); background: var(--editor-control); }
.editor-global-filter__panel { position: absolute; z-index: 3; top: calc(100% + 8px); left: 0; width: 380px; max-width: calc(100vw - 24px); max-height: calc(100vh - 64px); overflow-y: auto; border: 1px solid var(--editor-edge); border-radius: 5px; color: var(--editor-text); background: var(--editor-panel); box-shadow: 0 8px 24px #0004; font-size: 12px; }
.editor-global-filter__head { display: flex; align-items: center; gap: 8px; padding: 12px 10px 10px 14px; border-bottom: 1px solid var(--editor-edge); }
.editor-global-filter__head > div { flex: 1; }
.editor-global-filter__head strong { display: block; font-size: 13px; line-height: 20px; }
.editor-global-filter__head small { display: block; color: var(--editor-muted); font-size: 11px; line-height: 20px; }
.editor-global-filter__reset { min-height: 32px; padding: 0 6px; border: 0; background: transparent; color: var(--editor-accent); cursor: pointer; }
.editor-global-filter__controls { padding: 8px 14px 12px; }
.editor-global-filter__row { min-height: 51px; display: grid; grid-template-columns: 62px minmax(0, 1fr) 62px; align-items: center; gap: 10px; color: var(--editor-muted); }
.editor-global-filter__row input[type="number"] { width: 100%; height: 30px; min-width: 0; padding: 0 4px; border: 1px solid transparent; border-radius: 4px; background: var(--editor-control); color: var(--editor-text); text-align: center; font: inherit; }
.editor-global-filter__row input[type="number"]:focus-visible { outline: 2px solid #58a6ff; outline-offset: -2px; }
.editor-global-filter__row input[type="range"] { width: 100%; height: 22px; margin: 0; appearance: none; -webkit-appearance: none; border: 0; outline: 0; background: transparent; cursor: pointer; touch-action: pan-y; --range-track: linear-gradient(90deg, #777, #777); }
.editor-global-filter__row input[type="range"]::-webkit-slider-runnable-track { width: 100%; height: 5px; border-radius: 999px; background: var(--range-track); }
.editor-global-filter__row input[type="range"]::-webkit-slider-thumb { width: 14px; height: 14px; margin-top: -4.5px; appearance: none; -webkit-appearance: none; border: 1px solid #fff; border-radius: 50%; background: #1788f5; box-shadow: 0 0 0 1px #0003; }
.editor-global-filter__row input[type="range"]::-moz-range-track { height: 5px; border-radius: 999px; background: var(--range-track); }
.editor-global-filter__row input[type="range"]::-moz-range-progress { height: 5px; background: transparent; }
.editor-global-filter__row input[type="range"]::-moz-range-thumb { width: 14px; height: 14px; border: 1px solid #fff; border-radius: 50%; background: #1788f5; box-shadow: 0 0 0 1px #0003; }
.editor-global-filter__row input[type="range"]:focus-visible::-webkit-slider-thumb { box-shadow: 0 0 0 3px #258eff55; }
.editor-global-filter__row input[type="range"]:focus-visible::-moz-range-thumb { box-shadow: 0 0 0 3px #258eff55; }
.editor-global-filter__row input[data-filter="hue"] { --range-track: linear-gradient(90deg, #ff2424 0%, #ffd21a 17%, #29e948 34%, #15e6e2 50%, #1678ff 67%, #8d22ff 83%, #ff0068 100%); }
.editor-global-filter__row input[data-filter="saturation"] { --range-track: linear-gradient(90deg, #97999b, #a8878e 42%, #ff2838); }
.editor-global-filter__row input[data-filter="brightness"] { --range-track: linear-gradient(90deg, #030303, #767676 52%, #fff); }
.editor-global-filter__row input[data-filter="contrast"] { --range-track: linear-gradient(90deg, #9a9a9a, #171717 48%, #fff); }
.editor-global-filter__row input[data-filter="opacity"] { --range-track: linear-gradient(90deg, #2b2d2f, #616467); }
.editor-global-filter__row input[data-filter="grayscale"] { --range-track: linear-gradient(90deg, #258eff 0%, #258eff var(--range-progress), #85888b var(--range-progress), #85888b 100%); }
@media (max-width: 760px) { .editor-global-filter__panel { position: fixed; top: 55px; left: 12px; } }
@media (max-width: 600px) { .editor-global-filter__panel { top: 92px; max-height: calc(100vh - 104px); } }
</style>
